/**
 * VorlagenBibliothek – Wiederverwendbare Brief-Vorlagen verwalten
 * Benutzer können Briefe als Vorlagen speichern und bei neuen Schreiben wiederverwenden
 */
import { useState, useEffect } from 'react';
import { 
  FileText, Plus, Trash2, Edit2, Copy, Search, Filter, 
  Folder, CheckCircle, Clock, RefreshCw, Download, X, Save
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

const KATEGORIEN = [
  { id: 'allgemein', label: 'Allgemein', color: 'bg-slate-100 text-slate-700' },
  { id: 'pflegekasse', label: 'Pflegekasse', color: 'bg-blue-100 text-blue-700' },
  { id: 'sozialamt', label: 'Sozialamt', color: 'bg-green-100 text-green-700' },
  { id: 'gericht', label: 'Betreuungsgericht', color: 'bg-purple-100 text-purple-700' },
  { id: 'custom', label: 'Eigene Vorlagen', color: 'bg-orange-100 text-orange-700' },
];

export default function VorlagenBibliothek({ onSelectVorlage, klientId }) {
  const [vorlagen, setVorlagen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterKategorie, setFilterKategorie] = useState('alle');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showApplyDialog, setShowApplyDialog] = useState(false);
  const [selectedVorlage, setSelectedVorlage] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    beschreibung: '',
    kategorie: 'custom',
    inhalt: ''
  });
  const [applyVariablen, setApplyVariablen] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadVorlagen();
  }, []);

  const loadVorlagen = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/schriftverkehr/vorlagen`);
      const data = await res.json();
      setVorlagen(data.vorlagen || []);
    } catch (e) {
      console.error('Fehler beim Laden der Vorlagen:', e);
      toast.error('Fehler beim Laden der Vorlagen');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateVorlage = async () => {
    if (!formData.name.trim() || !formData.inhalt.trim()) {
      toast.error('Name und Inhalt sind erforderlich');
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/schriftverkehr/vorlagen`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await res.json();
      
      if (data.success) {
        toast.success('Vorlage erstellt');
        setShowCreateDialog(false);
        setFormData({ name: '', beschreibung: '', kategorie: 'custom', inhalt: '' });
        loadVorlagen();
      } else {
        toast.error(data.error || 'Fehler beim Erstellen');
      }
    } catch (e) {
      toast.error('Fehler beim Erstellen der Vorlage');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateVorlage = async () => {
    if (!selectedVorlage) return;

    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/schriftverkehr/vorlagen/${selectedVorlage.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await res.json();
      
      if (data.success) {
        toast.success('Vorlage aktualisiert');
        setShowEditDialog(false);
        setSelectedVorlage(null);
        loadVorlagen();
      } else {
        toast.error(data.error || 'Fehler beim Aktualisieren');
      }
    } catch (e) {
      toast.error('Fehler beim Aktualisieren der Vorlage');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteVorlage = async (vorlageId) => {
    if (!confirm('Vorlage wirklich löschen?')) return;

    try {
      const res = await fetch(`${BACKEND_URL}/api/schriftverkehr/vorlagen/${vorlageId}`, {
        method: 'DELETE'
      });
      const data = await res.json();
      
      if (data.success) {
        toast.success('Vorlage gelöscht');
        loadVorlagen();
      } else {
        toast.error(data.error || 'Fehler beim Löschen');
      }
    } catch (e) {
      toast.error('Fehler beim Löschen der Vorlage');
    }
  };

  const handleApplyVorlage = async () => {
    if (!selectedVorlage || !klientId) {
      toast.error('Kein Klient ausgewählt');
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/schriftverkehr/vorlagen/${selectedVorlage.id}/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          klient_id: klientId,
          variablen_werte: applyVariablen
        })
      });
      const data = await res.json();
      
      if (data.success) {
        toast.success('Vorlage angewendet - Brief erstellt');
        setShowApplyDialog(false);
        setApplyVariablen({});
        if (onSelectVorlage) {
          onSelectVorlage(data.brief);
        }
      } else {
        toast.error(data.error || 'Fehler beim Anwenden');
      }
    } catch (e) {
      toast.error('Fehler beim Anwenden der Vorlage');
    } finally {
      setSaving(false);
    }
  };

  const openEditDialog = (vorlage) => {
    setSelectedVorlage(vorlage);
    setFormData({
      name: vorlage.name,
      beschreibung: vorlage.beschreibung || '',
      kategorie: vorlage.kategorie,
      inhalt: vorlage.inhalt
    });
    setShowEditDialog(true);
  };

  const openApplyDialog = (vorlage) => {
    setSelectedVorlage(vorlage);
    // Initialisiere Variablen-Werte mit leeren Strings
    const vars = {};
    (vorlage.variablen || []).forEach(v => { vars[v] = ''; });
    setApplyVariablen(vars);
    setShowApplyDialog(true);
  };

  const getKategorieInfo = (kategorie) => {
    return KATEGORIEN.find(k => k.id === kategorie) || KATEGORIEN[0];
  };

  // Filtern
  const filteredVorlagen = vorlagen.filter(v => {
    const matchesSearch = v.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (v.beschreibung || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesKategorie = filterKategorie === 'alle' || v.kategorie === filterKategorie;
    return matchesSearch && matchesKategorie;
  });

  return (
    <div className="space-y-4" data-testid="vorlagen-bibliothek">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Folder className="w-5 h-5 text-slate-600" />
          <h3 className="font-semibold text-slate-800">Vorlagen-Bibliothek</h3>
          <span className="text-sm text-slate-500">({vorlagen.length} Vorlagen)</span>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={loadVorlagen}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button 
            variant="default" 
            size="sm" 
            onClick={() => setShowCreateDialog(true)}
            data-testid="create-vorlage-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Neue Vorlage
          </Button>
        </div>
      </div>

      {/* Filter & Suche */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Vorlagen durchsuchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={filterKategorie} onValueChange={setFilterKategorie}>
          <SelectTrigger className="w-[180px]">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Kategorie" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="alle">Alle Kategorien</SelectItem>
            {KATEGORIEN.map(k => (
              <SelectItem key={k.id} value={k.id}>{k.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Vorlagen-Liste */}
      {loading ? (
        <div className="flex justify-center py-8">
          <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : filteredVorlagen.length === 0 ? (
        <div className="text-center py-8 text-slate-500">
          <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p>Keine Vorlagen gefunden</p>
          <p className="text-sm">Erstellen Sie Ihre erste Vorlage oder ändern Sie die Filterkriterien</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {filteredVorlagen.map(vorlage => {
            const katInfo = getKategorieInfo(vorlage.kategorie);
            return (
              <div 
                key={vorlage.id}
                className="bg-white border rounded-lg p-4 hover:shadow-md transition-shadow"
                data-testid={`vorlage-item-${vorlage.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium text-slate-800 truncate">{vorlage.name}</h4>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${katInfo.color}`}>
                        {katInfo.label}
                      </span>
                    </div>
                    {vorlage.beschreibung && (
                      <p className="text-sm text-slate-500 line-clamp-2">{vorlage.beschreibung}</p>
                    )}
                    <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(vorlage.erstellt_am).toLocaleDateString('de-DE')}
                      </span>
                      <span className="flex items-center gap-1">
                        <Copy className="w-3 h-3" />
                        {vorlage.verwendungen || 0}x verwendet
                      </span>
                      {vorlage.variablen?.length > 0 && (
                        <span className="text-blue-500">
                          {vorlage.variablen.length} Variable(n)
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    {klientId && (
                      <Button 
                        variant="default" 
                        size="sm"
                        onClick={() => openApplyDialog(vorlage)}
                        data-testid={`apply-vorlage-${vorlage.id}`}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" /> Anwenden
                      </Button>
                    )}
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => openEditDialog(vorlage)}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleDeleteVorlage(vorlage.id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Neue Vorlage erstellen</DialogTitle>
            <DialogDescription>
              Erstellen Sie eine wiederverwendbare Brief-Vorlage. Verwenden Sie [PLATZHALTER] für variable Inhalte.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="z.B. Antrag Pflegegrad"
                />
              </div>
              <div>
                <Label>Kategorie</Label>
                <Select 
                  value={formData.kategorie} 
                  onValueChange={(v) => setFormData({ ...formData, kategorie: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {KATEGORIEN.map(k => (
                      <SelectItem key={k.id} value={k.id}>{k.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Beschreibung</Label>
              <Input
                value={formData.beschreibung}
                onChange={(e) => setFormData({ ...formData, beschreibung: e.target.value })}
                placeholder="Kurze Beschreibung der Vorlage"
              />
            </div>
            
            <div>
              <Label>Inhalt *</Label>
              <Textarea
                value={formData.inhalt}
                onChange={(e) => setFormData({ ...formData, inhalt: e.target.value })}
                placeholder="Brief-Inhalt mit [PLATZHALTERN] für variable Werte..."
                className="min-h-[200px] font-mono text-sm"
              />
              <p className="text-xs text-slate-500 mt-1">
                Tipp: Verwenden Sie [KLIENT_NAME], [DATUM], [AKTENZEICHEN] etc. für automatische Ersetzung.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Abbrechen</Button>
            <Button onClick={handleCreateVorlage} disabled={saving}>
              {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Vorlage speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Vorlage bearbeiten</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
              <div>
                <Label>Kategorie</Label>
                <Select 
                  value={formData.kategorie} 
                  onValueChange={(v) => setFormData({ ...formData, kategorie: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {KATEGORIEN.map(k => (
                      <SelectItem key={k.id} value={k.id}>{k.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Beschreibung</Label>
              <Input
                value={formData.beschreibung}
                onChange={(e) => setFormData({ ...formData, beschreibung: e.target.value })}
              />
            </div>
            
            <div>
              <Label>Inhalt *</Label>
              <Textarea
                value={formData.inhalt}
                onChange={(e) => setFormData({ ...formData, inhalt: e.target.value })}
                className="min-h-[200px] font-mono text-sm"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Abbrechen</Button>
            <Button onClick={handleUpdateVorlage} disabled={saving}>
              {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Apply Dialog */}
      <Dialog open={showApplyDialog} onOpenChange={setShowApplyDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Vorlage anwenden</DialogTitle>
            <DialogDescription>
              {selectedVorlage?.name} - Füllen Sie die Variablen aus
            </DialogDescription>
          </DialogHeader>
          
          {selectedVorlage?.variablen?.length > 0 ? (
            <div className="space-y-3">
              {selectedVorlage.variablen
                .filter(v => !['KLIENT_NAME', 'KLIENT_VORNAME', 'KLIENT_NACHNAME', 'DATUM', 'JAHR'].includes(v))
                .map(varName => (
                  <div key={varName}>
                    <Label className="text-sm">{varName.replace(/_/g, ' ')}</Label>
                    <Input
                      value={applyVariablen[varName] || ''}
                      onChange={(e) => setApplyVariablen({ ...applyVariablen, [varName]: e.target.value })}
                      placeholder={`Wert für [${varName}]`}
                    />
                  </div>
                ))}
              <p className="text-xs text-slate-500">
                Hinweis: Klient-Daten (Name, Aktenzeichen etc.) und Datum werden automatisch eingesetzt.
              </p>
            </div>
          ) : (
            <p className="text-sm text-slate-500">
              Diese Vorlage hat keine zusätzlichen Variablen. Klient-Daten werden automatisch eingesetzt.
            </p>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApplyDialog(false)}>Abbrechen</Button>
            <Button onClick={handleApplyVorlage} disabled={saving}>
              {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Brief erstellen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
